import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, Wallet, ArrowUpCircle, ArrowDownCircle, History, Settings, 
  Bell, Copy, Check, Phone, Mail, QrCode, Shield, HelpCircle, LogOut
} from "lucide-react";

export default function CoinPaySettings() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('light');
  const [showBrowser, setShowBrowser] = useState(true);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    const browserParam = searchParams.get('browser');
    
    if (themeParam) setBrowserTheme(themeParam);
    if (browserParam === 'false') setShowBrowser(false);
    
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setData(parsedData);
        // Check showBrowser from data
        if (parsedData.showBrowser === false) setShowBrowser(false);
        if (parsedData.browserTheme) setBrowserTheme(parsedData.browserTheme);
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
    document.title = 'Настройки | CoinPay';
    
    // Set body background to match content (fixes zoom issue)
    document.body.style.backgroundColor = '#f8fafc';
    document.documentElement.style.backgroundColor = '#f8fafc';
    
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/coinpay-favicon.svg';
    
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [searchParams]);

  if (!data) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Загрузка...</div>;
  }

  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '000000';
  const phone = data.phone || '+7 (999) 123-45-67';
  const email = data.email || 'user@example.com';

  const menuItems = [
    { icon: Home, label: 'Главная', active: false },
    { icon: Wallet, label: 'Кошелёк', active: false },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowDownCircle, label: 'Вывести', active: false },
    { icon: History, label: 'История', active: false },
    { icon: QrCode, label: 'QR-код', active: false },
    { icon: Shield, label: 'Безопасность', active: false },
    { icon: Settings, label: 'Настройки', active: true },
  ];

  const bottomItems = [
    { icon: HelpCircle, label: 'Поддержка' },
    { icon: LogOut, label: 'Выйти' },
  ];

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/coinpay-favicon.svg" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>Настройки | CoinPay</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>coinpay.ru/settings</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: showBrowser ? theme.bg : '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', overflow: 'hidden' }}>
      {showBrowser && <BrowserFrame />}
      
      <div className="flex-1 flex min-h-0" style={{ backgroundColor: '#f8fafc' }}>
        {/* Sidebar */}
        <aside className="w-56 bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col flex-shrink-0">
          <div className="p-4 flex items-center gap-2">
            <img src="/coinpay-logo-white.svg" alt="CoinPay" className="h-9 w-auto" style={{ maxWidth: '180px' }} />
          </div>

          <nav className="flex-1 py-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a key={index} href="#" className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${item.active ? 'text-[#FFD700] bg-white/10' : 'text-gray-300 hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </a>
              );
            })}
            
            <div className="mt-4 border-t border-white/10 pt-2">
              {bottomItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <a key={index} href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:bg-white/5">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </a>
                );
              })}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">Настройки профиля</h1>
            <div className="flex items-center gap-4">
              <button className="p-2 text-gray-500 hover:text-gray-700 relative">
                <Bell className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</span>
                </div>
                <span className="text-sm font-medium">{accountName}</span>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-2xl">
              {/* Verification Status Block */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-green-800 text-lg">Аккаунт верифицирован</div>
                    <div className="text-sm text-green-600">Полный доступ ко всем функциям платформы</div>
                  </div>
                  <div className="bg-green-500 text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    <span>Подтверждён</span>
                  </div>
                </div>
              </div>

              {/* Profile Info */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Личные данные</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Имя аккаунта</label>
                    <div className="relative">
                      <input type="text" value={accountName} readOnly className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 bg-white focus:outline-none" />
                      <button className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-[#FFD700] rounded flex items-center justify-center">
                        <Check className="w-4 h-4 text-[#1a1a2e]" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-1">ID пользователя</label>
                    <div className="flex items-center gap-2">
                      <input type="text" value={userId} readOnly className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-gray-900 bg-gray-50" />
                      <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <Copy className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Номер телефона</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <Phone className="w-5 h-5 text-gray-400" />
                      </div>
                      <input type="text" value={phone} readOnly className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-gray-900 bg-white" />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-green-500 text-sm">
                        <Check className="w-4 h-4" />
                        <span>Подтверждён</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Email</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <Mail className="w-5 h-5 text-gray-400" />
                      </div>
                      <input type="text" value={email} readOnly className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-gray-900 bg-white" />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-green-500 text-sm">
                        <Check className="w-4 h-4" />
                        <span>Подтверждён</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Безопасность</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">2FA аутентификация</p>
                        <p className="text-sm text-gray-500">Двухфакторная защита включена</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Активно</span>
                    </div>
                  </div>

                  <button className="w-full py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">
                    Изменить пароль
                  </button>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="bg-white rounded-xl border border-red-200 p-6">
                <h2 className="text-lg font-semibold text-red-600 mb-4">Опасная зона</h2>
                <p className="text-sm text-gray-500 mb-4">После удаления аккаунта все данные будут безвозвратно утеряны.</p>
                <button className="w-full py-3 border border-red-300 text-red-500 rounded-lg hover:bg-red-50">
                  Удалить аккаунт
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
